import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { GridsterComponentInterface } from 'angular-gridster2';
import { Chance } from 'chance';
import _ from 'lodash';
import moment from 'moment';
import { iif, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { gridDefaultValue } from 'src/app/components/dashboard/brick-wall/data/grid-default-values';
import { mapBrickInstanceToGridster } from 'src/app/components/dashboard/brick-wall/helper/GridsterSamwinBrickConverter';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { GridsterService } from 'src/app/services/gridster/gridster.service';
import { ToastUserInfoNotification } from 'src/app/store/notification/notification-toastr/notification-toastr.actions';
import { BrickWallService } from 'src/generated/swagger';
import { BrickDefinition } from '../../../../samwebDescription/samweb/brickDefinition';
import { BrickLayoutType } from '../../../../samwebDescription/samweb/dashboard/brickLayoutType';
import { CloseAddBrickDialogAction } from '../../brick-menu/add-new-brick-dialog.actions';
import { PilotPanelState } from '../../pilot-panel/pilot-panel.state';
import { PersistAllGridFilterSettings } from '../brick-behaviour-state/brick-filter-state/grid-filter.actions';
import { PersistAllGridColumnSettings } from '../brick-behaviour-state/brick-grids-state/brick-grids.actions';
import { PersistAllGridSortSettings } from '../brick-behaviour-state/brick-sort-state/grid-sort.actions';
import { PersistAllTabConfigurations } from '../brick-behaviour-state/brick-tab-state/brick-tab.actions';
import { BrickDefinitionState } from '../brick-definition/brick-definition.state';
import { CloseBrickSettingsDialog } from '../brick-settings/brick-settings.actions';
import { SaveAllChangedBrickSettings } from '../brick-settings/common-brick-settings/common-brick-settings.actions';
import { RemoveBrickFromInnerWallBrick } from '../brick-settings/specific-brick-settings/inner-wall-brick-settings/inner-wall-brick-settings.actions';
import { RemoveTabForTabBrick } from '../brick-settings/specific-brick-settings/tab-brick-settings/tab-brick-settings.actions';
import { BrickInstance } from '../brick-wall.state';
import {
   AddNewBrickToWallAction,
   BrickItemChangedOnWallAction,
   BrickWallChangedByServerAction,
   BrickWallInstancesChangedAction,
   LayoutBrickAddedToWall,
   RemoveBrickFromWall,
   SaveBrickWallChanges,
   StartBrickEditing,
   StartEditingBrickSettings,
   StopBrickEditing,
   StopEditingBrickSettings,
} from './brick-instances.actions';

export interface BrickInstancesStateModel {
   brickInstancesEditMode: { [instanceId: string]: BrickInstance };
   brickInstances: { [instanceId: string]: BrickInstance };
   isWallEditingEnabled: boolean;
   isSavingBrickWall: boolean;
}

@State<BrickInstancesStateModel>({
   name: 'brickInstances',
   defaults: {
      brickInstancesEditMode: {},
      brickInstances: {},
      isWallEditingEnabled: false,
      isSavingBrickWall: false,
   },
})
@Injectable()
export class BrickInstancesState {
   private static removeBrick(ctx: StateContext<BrickInstancesStateModel>, brickInstanceId: string, gridsterService: GridsterService) {
      ctx.setState((state: BrickInstancesStateModel) => {
         delete state.brickInstancesEditMode[brickInstanceId];
         return state;
      });
      gridsterService.itemChanged();
   }

   constructor(private store: Store, private gridsterService: GridsterService, private brickWallService: BrickWallService) {}

   @Action(StartBrickEditing)
   public startBrickEditing(ctx: StateContext<BrickInstancesStateModel>, action: StartBrickEditing) {
      if (ctx.getState().isWallEditingEnabled) {
         return;
      }

      ctx.dispatch(new StartEditingBrickSettings());

      ctx.patchState({
         brickInstancesEditMode: ctx.getState().brickInstances,
         isWallEditingEnabled: true,
      });
   }

   @Action(StopBrickEditing)
   public stopBrickEditing(ctx: StateContext<BrickInstancesStateModel>, action: StopBrickEditing) {
      const currentState = ctx.getState();
      if (!currentState.isWallEditingEnabled || currentState.isSavingBrickWall) {
         return;
      }

      if (action.saveChanges) {
         ctx.patchState({
            isSavingBrickWall: true,
         });
      }

      return iif(
         () => action.saveChanges,
         ctx.dispatch(new SaveBrickWallChanges()).pipe(
            switchMap(() => {
               const brickIds = _(currentState.brickInstancesEditMode)
                  .filter((brickInstance) => brickInstance.brickDefinitionId === 'tabBrick')
                  .map((brickInstance) => brickInstance.brickInstanceId)
                  .value();

               return ctx.dispatch(new PersistAllTabConfigurations(brickIds));
            }),
            switchMap(() => {
               // NOTE: Save all current column settings to overwrite changes made in SaveBrickWallChange
               const brickIds = _.keys(currentState.brickInstancesEditMode);
               return ctx.dispatch(new PersistAllGridColumnSettings(brickIds));
            }),
            switchMap(() => {
               const brickIds = _.keys(currentState.brickInstancesEditMode);
               return ctx.dispatch(new SaveAllChangedBrickSettings(brickIds));
            }),
            switchMap(() => {
               // NOTE: persist filter again to avoid losing them on changing settings
               const brickIds = _.keys(currentState.brickInstancesEditMode);
               return ctx.dispatch(new PersistAllGridFilterSettings(brickIds));
            }),
            switchMap(() => {
               // NOTE: persist sort again to avoid losing them on changing settings
               const brickIds = _.keys(currentState.brickInstancesEditMode);
               return ctx.dispatch(new PersistAllGridSortSettings(brickIds));
            }),
            tap(() =>
               ctx.dispatch(
                  new ToastUserInfoNotification(
                     NotificationType.INFO,
                     'notification.saving-settings-succeeded.title',
                     'notification.saving-settings-succeeded.message'
                  )
               )
            )
         ),
         of(null)
      ).pipe(
         catchError((error) => {
            console.error('Error during saveBrickWallChanges', error);
            return ctx.dispatch(
               new ToastUserInfoNotification(NotificationType.ERROR, 'notification.saving-settings-failed.title', 'notification.saving-settings-failed.message')
            );
         }),
         finalize(() => {
            ctx.patchState({
               brickInstances: action.saveChanges ? currentState.brickInstancesEditMode : currentState.brickInstances,
               brickInstancesEditMode: {},
               isWallEditingEnabled: false,
               isSavingBrickWall: false,
            });
            ctx.dispatch(new StopEditingBrickSettings());
            ctx.dispatch(new CloseAddBrickDialogAction());
            ctx.dispatch(new CloseBrickSettingsDialog(false));
         })
      );
   }

   @Action(SaveBrickWallChanges)
   public saveBrickWallChanges(ctx: StateContext<BrickInstancesStateModel>, action: SaveBrickWallChanges) {
      const state: BrickInstancesStateModel = this.store.selectSnapshot(BrickInstancesState);
      if (!state.isWallEditingEnabled) {
         return;
      }

      return this.brickWallService
         .postBrickWallDefinition(
            {
               brickWall: _.values(state.brickInstancesEditMode).map((x) => ({
                  brickDefinitionId: x.brickDefinitionId,
                  brickInstanceId: x.brickInstanceId,
                  positionY: x.positionY,
                  positionX: x.positionX,
                  rowHeight: x.rowHeight,
                  colWidth: x.colWidth,
                  brickLayout: x.brickLayout.value,
               })),
            },
            'response'
         )
         .pipe();
   }

   @Action(BrickItemChangedOnWallAction)
   @ImmutableContext()
   public brickItemChangedOnWall(ctx: StateContext<BrickInstancesStateModel>, action: BrickItemChangedOnWallAction) {
      ctx.setState((state) => {
         if (!state.isWallEditingEnabled) {
            return state;
         }
         const instanceForChange: BrickInstance = state.brickInstancesEditMode[action.instanceId];
         const container = gridDefaultValue(false);
         const adjustedWidth = action.gridsterInstance.cols < action.gridsterInstance.minCols ? action.gridsterInstance.minCols : action.gridsterInstance.cols;
         const adjustedHeight = action.gridsterInstance.rows < action.gridsterInstance.minRows ? action.gridsterInstance.minRows : action.gridsterInstance.rows;
         if (action.gridsterInstance.dragEnabled) {
            instanceForChange.positionX =
               container.maxItemCols < action.gridsterInstance.x + adjustedWidth
                  ? container.maxItemCols - action.gridsterInstance.minCols
                  : action.gridsterInstance.x;
            instanceForChange.positionY =
               container.maxItemRows < action.gridsterInstance.y + adjustedHeight
                  ? container.maxItemRows - action.gridsterInstance.minRows
                  : action.gridsterInstance.y;
         }
         if (action.gridsterInstance.resizeEnabled) {
            instanceForChange.colWidth = adjustedWidth;
            instanceForChange.rowHeight = adjustedHeight;
         }
         instanceForChange.brickLayout = action.brickLayout;
         // NOTE: forcefully trigger a change when minsize is set multiple times
         instanceForChange.updated = moment.now();
         state.brickInstancesEditMode[action.instanceId] = instanceForChange;
         return state;
      });
      this.gridsterService.itemChanged();
   }

   @Action(BrickWallInstancesChangedAction)
   @ImmutableContext()
   public brickWallInstancesChanged(ctx: StateContext<BrickInstancesStateModel>, action: BrickWallInstancesChangedAction) {
      ctx.setState((state: BrickInstancesStateModel) => {
         state.brickInstances = _.chain(action.instances)
            .keyBy((x) => x.brickInstanceId)
            .mapValues((x) => x)
            .value();
         return state;
      });
   }

   @Action(AddNewBrickToWallAction)
   @ImmutableContext()
   public addNewBrickToWall(ctx: StateContext<BrickInstancesStateModel>, action: AddNewBrickToWallAction) {
      let newBrickInstance: BrickInstance = null;
      ctx.setState((state: BrickInstancesStateModel) => {
         const chance = new Chance();
         if (!state.isWallEditingEnabled) {
            return state;
         }

         const brickDefinition = this.store.selectSnapshot(BrickDefinitionState.brickDefinitionByIdFn)(action.brickDefinitionId);
         if (_.isNil(brickDefinition)) {
            return state;
         }

         newBrickInstance = <BrickInstance>{
            brickInstanceId: chance.string({ length: 10, casing: 'lower', alpha: true, numeric: true }),
            brickDefinitionId: action.brickDefinitionId,
            brickLayout: action.brickLayout,
            positionY: 0,
            positionX: 0,
            rowHeight: brickDefinition.minRowHeight,
            colWidth: brickDefinition.minColWidth,
         };

         // check if there is enough space left for the new brick (fast adding bricks may result in adding bricks without space left)
         const isPilotPanelExtended = this.store.selectSnapshot(PilotPanelState.isPilotPanelExtended);
         const newGridsterItem = mapBrickInstanceToGridster(true, isPilotPanelExtended, { brickInstance: newBrickInstance, definition: brickDefinition });
         let gridster: GridsterComponentInterface = null;
         if (action.brickLayout === BrickLayoutType.PILOT_PANEL) {
            gridster = this.gridsterService.get(true);
         } else if (action.brickLayout === BrickLayoutType.DEFAULT_WALL) {
            gridster = this.gridsterService.get(false);
         } else {
            gridster = this.gridsterService.get(action.responsibleLayoutBrick);
         }
         if (!_.isNil(gridster) && !gridster.options.api.getNextPossiblePosition(newGridsterItem)) {
            this.store.dispatch(
               new ToastUserInfoNotification(
                  NotificationType.INFO,
                  'notification.bricks.add-failed-no-more-space.title',
                  'notification.bricks.add-failed-no-more-space.message'
               )
            );
            return state;
         }

         state.brickInstancesEditMode[newBrickInstance.brickInstanceId] = newBrickInstance;
         return state;
      });
      if (newBrickInstance.brickLayout === BrickLayoutType.IN_LAYOUT_BRICK) {
         return this.store.dispatch(new LayoutBrickAddedToWall(newBrickInstance.brickInstanceId, action.responsibleLayoutBrick));
      }
   }

   @Action(BrickWallChangedByServerAction)
   public brickWallChangedByServer(ctx: StateContext<BrickInstancesStateModel>, action: BrickWallChangedByServerAction) {
      const mappedInstances: BrickInstance[] = _.map(
         action.bricks,
         (x) =>
            <BrickInstance>{
               brickDefinitionId: x.brickDefinitionId,
               brickInstanceId: x.brickInstanceId,
               positionY: x.positionY,
               positionX: x.positionX,
               rowHeight: x.rowHeight,
               colWidth: x.colWidth,
               brickLayout: BrickLayoutType.valueOf(x.brickLayout),
            }
      );
      this.store.dispatch(new BrickWallInstancesChangedAction(mappedInstances));
   }

   @Action(RemoveBrickFromInnerWallBrick)
   @ImmutableContext()
   public removeBrickFromInnerWallBrick(ctx: StateContext<BrickInstancesStateModel>, action: RemoveBrickFromInnerWallBrick) {
      BrickInstancesState.removeBrick(ctx, action.brickInstanceIdToRemove, this.gridsterService);
   }

   @Action(RemoveTabForTabBrick)
   @ImmutableContext()
   public removeTabForTabBrick(ctx: StateContext<BrickInstancesStateModel>, action: RemoveTabForTabBrick) {
      BrickInstancesState.removeBrick(ctx, action.brickInstanceIdToRemove, this.gridsterService);
   }

   @Action(RemoveBrickFromWall)
   @ImmutableContext()
   public removeBrickFromWall(ctx: StateContext<BrickInstancesStateModel>, action: RemoveBrickFromWall) {
      BrickInstancesState.removeBrick(ctx, action.brickInstanceId, this.gridsterService);
   }
}

export interface IBrickDescription {
   brickInstance: BrickInstance;
   definition: BrickDefinition;
}
